import { yupResolver } from '@hookform/resolvers/yup';
import LockOutlined from '@mui/icons-material/LockOutlined';
import {
  ConfirmPasswordField,
  ConfirmResetPasswordPageSchema,
  PasswordField,
  PasswordResetCode,
  UserEmailFields,
} from '@src/authentication/ConfirmResetPasswordFields';
import { useConfirmResetPassword } from '@src/authentication/useConfirmResetPassword';
import { Button } from '@src/common/components/button/Button';
import { Header } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { colors } from '@src/common/styles/valueTokens';
import { ControllerFormTextInput } from '@src/orders/PermitFormSections/ControllerFormTextInput';
import { Navigation } from '@src/routing/Navigation';
import { t } from 'i18next';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { InferType } from 'yup';

export const ConfirmResetPasswordPage = () => {
  const navigate = useNavigate();
  const { showSuccessToast, showErrorToast } = useToast();
  const [searchParam] = useSearchParams();

  const defaultEmail = searchParam.get('email') ?? '';
  const defaultCode = searchParam.get('code') ?? '';

  const { confirmResetPassword, isLoading } = useConfirmResetPassword({
    onSuccess: () => {
      showSuccessToast(t('confirmResetPassword.success'));
      navigate(Navigation.login);
    },
    onError: () => {
      showErrorToast(t('confirmResetPassword.error'));
    },
  });

  const { handleSubmit, control } = useForm({
    resolver: yupResolver(ConfirmResetPasswordPageSchema),
    defaultValues: {
      [UserEmailFields.fieldName]: defaultEmail,
      [PasswordResetCode.fieldName]: defaultCode,
    },
  });

  const handleConfirmResetPassword: SubmitHandler<
    InferType<typeof ConfirmResetPasswordPageSchema>
  > = async (data) => {
    confirmResetPassword({
      userEmail: data[UserEmailFields.fieldName],
      resetPasswordAttemptCode: data[PasswordResetCode.fieldName],
      newPassword: data[PasswordField.fieldName],
    });
  };

  return (
    <Stack className={page}>
      <Stack width={{ xs: '272px', md: '360px' }}>
        <form onSubmit={handleSubmit(handleConfirmResetPassword)}>
          <Stack gap={theme.spacing['4']} marginBottom={theme.spacing['6']}>
            <LockOutlined fontSize="large" />
            <Header title={t('confirmResetPassword.title')} />
            <Typography style={{ color: colors.text.secondary }}>
              {t('confirmResetPassword.subtitle')}
            </Typography>
          </Stack>
          <Stack gap={theme.spacing['4']} width={{ xs: '272px', md: '310px' }}>
            <ControllerFormTextInput
              control={control}
              disabled={!!defaultEmail || isLoading}
              fieldName={UserEmailFields.fieldName}
              autoComplete="new-email"
            />
            <ControllerFormTextInput
              control={control}
              disabled={isLoading}
              fieldName={PasswordField.fieldName}
              type="password"
              autoComplete="new-password"
              label={t('newPassword')}
            />
            <ControllerFormTextInput
              control={control}
              disabled={isLoading}
              fieldName={ConfirmPasswordField.fieldName}
              type="password"
              autoComplete="confirm-new-password"
              label={t('confirmPassword')}
            />
            <ControllerFormTextInput
              control={control}
              disabled={!!defaultCode || isLoading}
              fieldName={PasswordResetCode.fieldName}
              autoComplete="one-time-code"
            />
          </Stack>
          <Stack marginTop={theme.spacing['12']}>
            <Button variant="contained" type="submit">
              {t('submit')}
            </Button>
          </Stack>
        </form>
      </Stack>
    </Stack>
  );
};

const page = css(() => ({
  height: '100vh',
  backgroundColor: 'white',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));
